import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

function useCountUp(target, inView, duration = 2200) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, target, duration]);
  return count;
}

const stats = [
  { value: 150, suffix: 'B', prefix: '$', label: 'Robotics market by 2035', sublabel: 'Total addressable market' },
  { value: 12, suffix: 'B', prefix: '$', label: 'On-robot compute TAM', sublabel: 'Edge AI processing' },
  { value: 100, suffix: 'K+', prefix: '', label: 'Humanoid units by 2030', sublabel: 'Goldman Sachs projection' },
  { value: 0, suffix: '', prefix: '', label: 'Viable trust layers', sublabel: 'In the market today' },
];

function StatItem({ stat, inView }) {
  const count = useCountUp(stat.value, inView);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="border-l border-[#00F0FF]/20 pl-6"
      data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div
        className="font-display font-bold text-white leading-none mb-2 gradient-text"
        style={{ fontSize: 'clamp(42px, 5vw, 64px)', fontFamily: "'Syne', sans-serif" }}
      >
        {stat.prefix}{inView ? count : 0}{stat.suffix}
      </div>
      <div className="text-white text-sm font-medium mb-1">{stat.label}</div>
      <div className="font-mono text-[#444] text-xs tracking-wider">{stat.sublabel}</div>
    </motion.div>
  );
}

export default function MarketSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="market"
      data-testid="market-section"
      className="relative overflow-hidden"
      style={{ background: '#000' }}
    >
      {/* Market Opportunity */}
      <div className="py-28 md:py-36 border-b border-white/5" ref={ref}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="font-mono text-[#00F0FF] text-xs tracking-[0.35em] uppercase mb-4">
              The Opportunity
            </p>
            <h2
              className="font-display text-white font-bold tracking-tight mb-4"
              style={{ fontSize: 'clamp(36px, 5vw, 60px)', fontFamily: "'Syne', sans-serif" }}
            >
              The $500B Robotics Market<br />
              <span className="text-[#666]">is Being Tokenized</span>
            </h2>
            <p className="text-[#555] max-w-xl text-base leading-relaxed">
              Everyone is shipping hardware. No one has built the trust layer that makes autonomous deployment possible at scale. NeuroMesh is that layer.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat) => (
              <StatItem key={stat.label} stat={stat} inView={inView} />
            ))}
          </div>
        </div>
      </div>

      {/* Cerebro Product */}
      <div className="py-28 md:py-36" style={{ background: '#0A0A0A' }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
            >
              <p className="font-mono text-[#00F0FF] text-xs tracking-[0.35em] uppercase mb-4">
                First Product
              </p>
              <h2
                className="font-display text-white font-bold tracking-tight mb-5"
                style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontFamily: "'Syne', sans-serif" }}
              >
                Cerebro
              </h2>
              <p className="text-[#777] text-base leading-relaxed mb-6 max-w-md">
                The first product operating on NeuroMesh infrastructure. A self-evolving personal AI platform co-developed by the NeuroMesh team — live in beta with real users, real revenue, and a clear roadmap.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Live beta with active users',
                  'Revenue generating',
                  'Token integration underway',
                  'Co-developed by NeuroMesh team',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-[#888]">
                    <span className="w-1.5 h-1.5 bg-[#00F0FF] rounded-full flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://cerebro.vip"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 btn-primary text-xs"
                  data-testid="cerebro-link"
                >
                  Visit Cerebro <ExternalLink size={12} />
                </a>
                <a
                  href="https://x.com/MeshNeuro/status/2025189029914776050"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 btn-secondary text-xs"
                  data-testid="ceo-intro-link"
                >
                  CEO Introduction <ExternalLink size={12} />
                </a>
              </div>
            </motion.div>

            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="relative"
            >
              <div
                className="relative rounded-none overflow-hidden"
                style={{ border: '1px solid rgba(0, 240, 255, 0.1)', background: '#0D0D0D', minHeight: '320px' }}
              >
                {/* Decorative terminal */}
                <div className="p-6 border-b border-white/5 flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FF3B3B] opacity-60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FFD700] opacity-60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#00FF88] opacity-60" />
                  <span className="font-mono text-[#444] text-xs ml-3">cerebro.vip — live beta</span>
                </div>
                <div className="p-8 font-mono text-sm space-y-3">
                  <div className="text-[#00FF88]">$ cerebro --status</div>
                  <div className="text-[#666]">Initializing Cerebro AI Platform...</div>
                  <div className="text-[#888]">  Connected to NeuroMesh Protocol</div>
                  <div className="text-[#888]">  User sessions: <span className="text-[#00F0FF]">active</span></div>
                  <div className="text-[#888]">  Revenue status: <span className="text-[#00FF88]">generating</span></div>
                  <div className="text-[#888]">  Token integration: <span className="text-[#FFD700]">in progress</span></div>
                  <div className="flex items-center text-[#00FF88] mt-4">
                    <span>Ready</span>
                    <span className="terminal-cursor ml-1" />
                  </div>
                </div>
                <div
                  className="absolute bottom-0 left-0 right-0 h-px"
                  style={{ background: 'linear-gradient(90deg, transparent, #00F0FF40, transparent)' }}
                />
              </div>
              {/* Glow */}
              <div
                className="absolute -bottom-4 left-0 right-0 h-16 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at center, rgba(0, 240, 255, 0.08) 0%, transparent 70%)' }}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
